import { useMemo } from "react";
import { Select, DatePicker } from "antd";
import dayjs from "dayjs";
import M2MSelector from "../M2MSelector";
import { withLocale } from "../../l10n";
import { translate } from "../../l10n/translate";
import { STATE_ACTIVE, STATE_CANCELLED, STATE_INACTIVE, STATE_NOT_APPLICABLE } from "../../utils/constants.js";

// Estados del ciclo de vida de la M2M
const optionsState = [
  { value: '', label: translate('Seleccionar opción'), disabled: true },
  { value: STATE_INACTIVE, label: translate('Lista para activación') },
  { value: STATE_ACTIVE, label: translate('Activo') },
  { value: STATE_CANCELLED, label: translate('Cancelado') },
  { value: STATE_NOT_APPLICABLE, label: translate('No procede') },
];

/**
 * Bloque completo de la suscripcion M2M: la SIM con su tarifa y el contrato
 * con su estado, sus fechas y su intervalo.
 *
 * No guarda nada ni tiene estado propio: recibe los valores y devuelve los
 * cambios en forma de parche.
 *
 * @param {Object}   value        { icc, state, hiring_date, renewal_date, renewal_interval }
 * @param {Function} onChange     recibe un parche de value
 * @param {Boolean}  editable     si no, todo bloqueado
 * @param {Boolean}  readOnly     estado y fechas de consulta; el ICC sigue editable
 */
function M2MSubscriptionFields({
  lang,
  value,
  onChange,
  editable,
  readOnly = false,
  suggestedOwner,
  suggestedAlias,
  defaultSimType,
  onKiteSaved,
}) {

  // ===== DERIVADOS =====
  const cancelledOrNotApplicable = useMemo(
    () => value.state === STATE_NOT_APPLICABLE
       || value.state === STATE_CANCELLED
       || value.state === '',
    [value.state]
  );

  const datesDisabled = !editable || readOnly || cancelledOrNotApplicable;

  // ===== HANDLERS =====
  const onChangeState = (state) => {
    onChange({
      state,
      ...(state === STATE_NOT_APPLICABLE ? { renewal_interval: 0 } : {}),
    });
  };

  const onChangeDate = (dateString, field) => {
    const patch = { [field]: dateString || null };
    if (field === 'hiring_date' && !value.renewal_date) {
      patch.renewal_date = dateString || null;
    }
    onChange(patch);
  };

  // ===== RENDER =====
  return (
    <>
      <M2MSelector
        icc={value.icc}
        onIccChange={(icc) => onChange({ icc })}
        suggestedOwner={suggestedOwner}
        suggestedAlias={suggestedAlias}
        defaultSimType={defaultSimType}
        disabled={!editable}
        onKiteSaved={onKiteSaved}
      />

      <div className="form-group row">
        <label htmlFor="state_m2m" className="col-sm-2 col-form-label newCard-form-text">
          {lang.handle('Estado')}
        </label>
        <div className="col-sm-10">
          <Select
            className="form-control"
            onChange={onChangeState}
            name="state_m2m"
            value={value.state || ''}
            disabled={!editable || readOnly}
            options={optionsState}
          />
        </div>
      </div>

      <div className="form-group row">
        <label htmlFor="hiring_date_m2m" className="col-sm-2 col-form-label newCard-form-text">
          {lang.handle('Fecha primera contratación')}
        </label>
        <div className="col-sm-10">
          <DatePicker
            name="hiring_date_m2m"
            className="form-control"
            value={value.hiring_date ? dayjs(value.hiring_date, "DD-MM-YYYY") : null}
            onChange={(date, dateString) => onChangeDate(dateString, 'hiring_date')}
            format="DD-MM-YYYY"
            disabled={datesDisabled}
          />
        </div>
      </div>

      <div className="form-group row">
        <label htmlFor="renewal_date_m2m" className="col-sm-2 col-form-label newCard-form-text">
          {lang.handle('Fecha última renovación')}
        </label>
        <div className="col-sm-10">
          <DatePicker
            name="renewal_date_m2m"
            className="form-control"
            value={value.renewal_date ? dayjs(value.renewal_date, "DD-MM-YYYY") : null}
            onChange={(date, dateString) => onChangeDate(dateString, 'renewal_date')}
            format="DD-MM-YYYY"
            disabled={datesDisabled}
          />
        </div>
      </div>

      <div className="form-group row">
        <label htmlFor="m2m_renewal_interval" className="col-sm-2 col-form-label newCard-form-text">
          {lang.handle('Intervalo de renovación')}
        </label>
        <div className="col-sm-10">
          <input
            type="number"
            className="form-control"
            id="m2m_renewal_interval"
            name="m2m_renewal_interval"
            min="0"
            max="120"
            step="1"
            value={value.renewal_interval}
            onChange={(e) => onChange({ renewal_interval: e.target.value })}
            disabled={datesDisabled}
          />
          <small className="form-text text-muted">{lang.handle('Ingrese el número de meses')}</small>
        </div>
      </div>
    </>
  );
}

export default withLocale(M2MSubscriptionFields);
